import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { TossPaymentMethod } from '../../../../consts';
import { BusinessPaymentPaypleCallbackUrl, BusinessPaymentTossFailCallbackUrl, BusinessPaymentTossSuccessCallbackUrl, usePayBusinessOrder } from '../../../../services';
import { paymentFailureFromError } from '../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog } from '../../payment-tracking/PaymentFailureContent';
import { PaypleProvider, usePayple } from '../../subscription/hooks/usePayple';
import { BusinessPaymentLayout } from '../BusinessPaymentLayout';
import { BusinessPaymentsInfo, BusinessPaymentsInfoProps } from './BusinessPaymentsInfo';
export const PayplePayBusinessPayment = (props: Omit<BusinessPaymentsInfoProps, 'onSubmit'>) => <PaypleProvider data-sentry-element="PaypleProvider" data-sentry-component="PayplePayBusinessPayment" data-sentry-source-file="PayBusinessPayment.tsx">
    <PayplePayBusinessPaymentContent {...props} data-sentry-element="PayplePayBusinessPaymentContent" data-sentry-source-file="PayBusinessPayment.tsx" />
  </PaypleProvider>;
const PayplePayBusinessPaymentContent = (props: Omit<BusinessPaymentsInfoProps, 'onSubmit'>) => {
  const {
    showWidget
  } = usePayple();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['common']);
  const showErrorDialog = (error: unknown, orderId: string) => {
    void createPaymentFailureDialog(dialog, {
      payment_flow: 'business_payment',
      pg_provider: 'payple',
      payment_reference_id: orderId ? `payple_order:${orderId}` : undefined,
      ...paymentFailureFromError(error)
    }, error).alert({
      title: t('common:error.api.unknown')
    });
    console.error('unknownError', error);
  };
  const payBusinessOrder = usePayBusinessOrder(props.paymentHashId);
  const onSubmit = async () => {
    let orderId = props.businessPayment.orderId;
    try {
      const res = await payBusinessOrder.mutateAsync();
      orderId = res.orderId;

      // PG amount는 서버 order snapshot의 finalPrice만 허용한다.
      // businessPayment.price(VAT 전 base)로 fallback해서 결제를 진행하면
      // 서버 callback amount 검증(Gate I-1)과 어긋나므로 결제를 차단한다.
      // 0원/음수도 PG 진입 금지 (Gate H).
      if (typeof res.finalPrice !== 'number' || res.finalPrice <= 0) {
        throw new Error('Business payment order의 finalPrice가 유효하지 않습니다. 서버 VAT snapshot 합류 전이거나 0원 이하 금액은 결제를 차단합니다.');
      }

      // 고객이 화면에서 동의한 금액(BusinessPaymentsInfo와 동일식: finalPrice ?? price)과
      // PG 청구 금액(order finalPrice)이 다르면 결제를 차단한다 — 표시 ≠ 청구 사고 방지.
      // 같은 snapshot이면 항상 일치해야 하며(Gate I-1), 불일치는 서버 계약 위반 신호다.
      const displayedAmount = props.businessPayment.finalPrice ?? props.businessPayment.price;
      if (res.finalPrice !== displayedAmount) {
        throw new Error(`Business payment 금액 불일치: 화면 표시 ${displayedAmount} ≠ 주문 금액 ${res.finalPrice}. 결제를 차단합니다.`);
      }
      await showWidget({
        orderId: res.orderId,
        type: 'business',
        comments: props.productName,
        totalAmount: res.finalPrice,
        businessPaymentHashId: props.paymentHashId,
        email: props.businessPayment.email,
        callbackUrl: BusinessPaymentPaypleCallbackUrl
      });
    } catch (e) {
      showErrorDialog(e, orderId);
    }
  };
  return <BusinessPaymentLayout title={props.businessName} data-sentry-element="BusinessPaymentLayout" data-sentry-component="PayplePayBusinessPaymentContent" data-sentry-source-file="PayBusinessPayment.tsx">
      <BusinessPaymentsInfo {...props} onSubmit={onSubmit} data-sentry-element="BusinessPaymentsInfo" data-sentry-source-file="PayBusinessPayment.tsx" />
    </BusinessPaymentLayout>;
};
export const TossPayBusinessPayment = (props: Omit<BusinessPaymentsInfoProps, 'onSubmit'>) => {
  const {
    paymentHashId,
    businessPayment,
    businessName,
    productName
  } = props;
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['common']);
  const showErrorDialog = (error: unknown, orderId: string) => {
    void createPaymentFailureDialog(dialog, {
      payment_flow: 'business_payment',
      pg_provider: 'toss',
      payment_reference_id: orderId ? `toss_order:${orderId}` : undefined,
      ...paymentFailureFromError(error)
    }, error).alert({
      title: t('common:error.api.unknown')
    });
    console.error('unknownError', error);
  };
  const payBusinessOrder = usePayBusinessOrder(paymentHashId);
  const onSubmit = async () => {
    let orderId = businessPayment.orderId;
    try {
      const orderInformation = await payBusinessOrder.mutateAsync();
      orderId = orderInformation.orderId;

      // PG amount는 서버 order snapshot의 finalPrice만 허용한다.
      // businessPayment.price(VAT 전 base) fallback 결제 금지 (Gate I-1).
      // 0원/음수도 PG 진입 금지 (Gate H).
      if (typeof orderInformation.finalPrice !== 'number' || orderInformation.finalPrice <= 0) {
        throw new Error('Business payment order의 finalPrice가 유효하지 않습니다. 서버 VAT snapshot 합류 전이거나 0원 이하 금액은 결제를 차단합니다.');
      }

      // 고객이 화면에서 동의한 금액(BusinessPaymentsInfo와 동일식: finalPrice ?? price)과
      // PG 청구 금액(order finalPrice)이 다르면 결제를 차단한다 — 표시 ≠ 청구 사고 방지.
      // 같은 snapshot이면 항상 일치해야 하며(Gate I-1), 불일치는 서버 계약 위반 신호다.
      const displayedAmount = businessPayment.finalPrice ?? businessPayment.price;
      if (orderInformation.finalPrice !== displayedAmount) {
        throw new Error(`Business payment 금액 불일치: 화면 표시 ${displayedAmount} ≠ 주문 금액 ${orderInformation.finalPrice}. 결제를 차단합니다.`);
      }
      window.TossPayments(process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY_FOR_BUSINESS).requestPayment(TossPaymentMethod.Card, {
        amount: orderInformation.finalPrice,
        orderId: orderInformation.orderId,
        orderName: productName,
        customerName: businessName,
        customerEmail: businessPayment.email,
        windowTarget: 'self',
        successUrl: BusinessPaymentTossSuccessCallbackUrl,
        failUrl: BusinessPaymentTossFailCallbackUrl,
        useInternationalCardOnly: false
      });
    } catch (e) {
      showErrorDialog(e, orderId);
    }
  };
  return <BusinessPaymentLayout title={props.businessName} data-sentry-element="BusinessPaymentLayout" data-sentry-component="TossPayBusinessPayment" data-sentry-source-file="PayBusinessPayment.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="PayBusinessPayment.tsx">
        <script async src="https://js.tosspayments.com/v1"></script>
      </Head>
      <BusinessPaymentsInfo {...props} onSubmit={onSubmit} data-sentry-element="BusinessPaymentsInfo" data-sentry-source-file="PayBusinessPayment.tsx" />
    </BusinessPaymentLayout>;
};